import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Field } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  getClaimTokenState,
  completeAccountClaim,
} from "@/lib/claim.functions";

export const Route = createFileRoute("/claim/$token")({
  head: () => ({
    meta: [
      { title: "Set your password: CapitalDart" },
      {
        name: "description",
        content:
          "Set your password to access your CapitalDart account.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Set your password: CapitalDart" },
      {
        property: "og:description",
        content: "Set your password to access your CapitalDart account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ClaimPage,
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-5 py-16">
      {children}
    </main>
  );
}

function ClaimPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const fetchState = useServerFn(getClaimTokenState);
  const submitClaim = useServerFn(completeAccountClaim);

  const q = useQuery({
    queryKey: ["claim-token", token],
    queryFn: () => fetchState({ data: { token } }),
    retry: false,
  });

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const result = await submitClaim({ data: { token, password } });
      if (!result.ok) {
        setLoading(false);
        setDone(true);
        void q.refetch();
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: result.email,
        password,
      });
      setLoading(false);
      if (signInError) {
        setDone(true);
        return;
      }
      void navigate({ to: "/dashboard" });
    } catch (err) {
      setLoading(false);
      setError(
        err instanceof Error ? err.message : "Something went wrong. Try again.",
      );
    }
  }

  if (q.isPending) {
    return (
      <Shell>
        <p className="text-sm text-brand-muted">Loading.</p>
      </Shell>
    );
  }

  const state = q.data;

  if (done || state?.status === "claimed") {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">Your account is set up.</h1>
        <p className="mt-2 text-sm text-brand-muted">
          Sign in with your email address and the password you chose.
        </p>
        <Link
          to="/auth"
          search={{ mode: "signin" as const }}
          className="mt-6 inline-block rounded-md bg-brand-ink px-4 py-2 text-sm font-medium text-white"
        >
          Go to sign in
        </Link>
      </Shell>
    );
  }

  if (!state || state.status !== "valid") {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">This link is no longer active.</h1>
        <p className="mt-2 text-sm text-brand-muted">
          This link has expired or was already used. Reply to the email that
          sent you this link and we will send a fresh one.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-2xl font-semibold">Set your password.</h1>
      <p className="mt-2 text-sm text-brand-muted">
        Set your password to access your CapitalDart account.
      </p>
      <p className="mt-4 text-sm">
        <span className="text-brand-muted">Account: </span>
        <span className="font-medium">{state.email}</span>
      </p>
      <form onSubmit={onSubmit} noValidate className="mt-5">
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        {error && (
          <p className="mt-3 text-sm text-red-600" aria-live="polite">
            {error}
          </p>
        )}
        <Button type="submit" disabled={loading} className="mt-5 w-full">
          {loading ? "Setting up." : "Set my password and sign in"}
        </Button>
      </form>
    </Shell>
  );
}
